import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, KeyRound, QrCode, RefreshCw, X } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import { Cargando } from '../ui/EstadoDePantalla';
import { usePaso, useSubpantalla } from '../ui/navegacionReact';
import {
  LARGO_CODIGO, LARGO_NOMBRE, codigoCompleto, faltaEnDonde, formatearCodigo, minutosYSegundos, motivoDeRechazo,
  normalizarCodigo, recortarNombre, useCuentaRegresiva,
} from './codigoDeTerminal';
import { leerErrorDelContrato } from './errorDelContrato';

/* =====================================================================
   «Agregar terminal» · DISEÑO-F1 §7 · contrato v2 §2.3 y §2.4
   =====================================================================
   Tres pasos: 1 el CÓDIGO que enseña la terminal, 2 DÓNDE va a trabajar
   (sucursal, repisa y nombre) y 3 LISTO. El código puede venir ya puesto
   desde el QR de la terminal (`/t/<código>` → `contexto.vincular`): en
   ese caso se salta al paso 2 solo.

   Y LA OTRA PUERTA, «Tengo un código en la terminal»: para una terminal
   sin cámara cerca, TEGO da un número de 6 dígitos que se teclea en el
   aparato, en su opción «Tengo un código». Vale 10 minutos y una sola
   vez; por eso se pide con la terminal delante.

   TODO SE VE EN UNA COLUMNA DESDE 320 px: pasos apilados, botones a todo
   lo ancho, el número grande y centrado, nada flota.
   ===================================================================== */

const PASOS = [
  { n: 1, nombre: 'Código' },
  { n: 2, nombre: 'Dónde' },
  { n: 3, nombre: 'Listo' },
];

const BOTON = 'min-h-11 w-full rounded-xl px-3 text-sm font-semibold disabled:opacity-50';
const PRIMARIO = `${BOTON} bg-[var(--color-marca)] text-white`;
const SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800`;
const ENLACE = 'min-h-11 inline-flex items-center gap-1 text-sm font-semibold text-gray-700';

const PESTANAS = {
  TERMINAL: 'La terminal me enseña un código',
  ALTA: 'Tengo un código en la terminal',
};

const ERRORES_VER = { 404: 'CODIGO_DESCONOCIDO', 410: 'CODIGO_VENCIDO', 429: 'DEMASIADAS_PETICIONES' };
const ERRORES_APROBAR = { 404: 'CODIGO_DESCONOCIDO', 409: 'YA_VINCULADA', 403: 'SIN_PERMISO', 429: 'DEMASIADAS_PETICIONES' };

function BarraPasos({ paso }) {
  const actual = PASOS.find((p) => p.n === paso) || PASOS[0];
  return (
    <nav aria-label="Progreso de la vinculación" data-barra-pasos="" data-paso-actual={paso}
         className="rounded-xl border border-gray-200 bg-white p-3">
      <p className="text-sm font-semibold text-gray-900">{`Paso ${paso} de ${PASOS.length} · ${actual.nombre}`}</p>
      <div className="mt-2 grid grid-cols-3 gap-1" aria-hidden="true">
        {PASOS.map((p) => (
          <div key={p.n} className={`h-2 rounded-full ${p.n <= paso ? 'bg-[var(--color-marca)]' : 'bg-gray-200'}`} />
        ))}
      </div>
      <ol className="mt-2 grid grid-cols-3 gap-x-2 text-xs">
        {PASOS.map((p) => (
          <li key={p.n} className={`flex items-start gap-1 break-words ${p.n === paso ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
            {p.n < paso && <Check className="mt-0.5 h-3 w-3 shrink-0 text-green-700" aria-hidden="true" />}
            <span className="min-w-0">{p.n}. {p.nombre}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Qué pasó y qué hacer, en una caja roja. */
export function ErrorDelContrato({ error, id }) {
  if (!error) return null;
  return (
    <div id={id} role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
      <strong className="block break-words">{error.mensaje}</strong>
      <p className="mt-1 break-words text-red-800">{error.queHacer}</p>
    </div>
  );
}

// ---------------------------------------------------------------------
// Dónde va a trabajar: sucursal (si hay más de una), repisa y nombre.
// Una terminal es UNA estación (R-TP5): la repisa es obligatoria.
// ---------------------------------------------------------------------
export function CamposDonde({ idBase, sucursales = [], ubicaciones = [], valor, onCambio }) {
  const sucursal = valor.id_sucursal || '';
  const visibles = sucursal
    ? ubicaciones.filter((u) => !u.id_sucursal || String(u.id_sucursal) === String(sucursal))
    : ubicaciones;
  const nombre = valor.nombre || '';
  return (
    <div className="space-y-3">
      {sucursales.length > 1 && (
        <div>
          <label htmlFor={`${idBase}-sucursal`} className="block text-sm font-medium text-gray-700">En qué sucursal</label>
          <select id={`${idBase}-sucursal`} className="entrada mt-1" value={sucursal}
                  onChange={(e) => onCambio({ ...valor, id_sucursal: e.target.value, id_ubicacion: '' })}>
            <option value="">Todas</option>
            {sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </div>
      )}
      <div>
        <label htmlFor={`${idBase}-ubicacion`} className="block text-sm font-medium text-gray-700">En qué repisa trabaja</label>
        <select id={`${idBase}-ubicacion`} className="entrada mt-1" value={valor.id_ubicacion || ''}
                onChange={(e) => {
                  const u = ubicaciones.find((x) => String(x.id) === e.target.value);
                  onCambio({ ...valor, id_ubicacion: e.target.value,
                             id_sucursal: u?.id_sucursal ? String(u.id_sucursal) : valor.id_sucursal });
                }}>
          <option value="">Elige una…</option>
          {visibles.map((u) => <option key={u.id} value={u.id}>{u.codigo} · {u.nombre}</option>)}
        </select>
        <p className="mt-1 text-xs text-gray-500">La terminal solo ve y mueve lo que está en esta repisa.</p>
      </div>
      <div>
        <label htmlFor={`${idBase}-nombre`} className="block text-sm font-medium text-gray-700">Cómo se llama</label>
        <input id={`${idBase}-nombre`} className="entrada mt-1" value={nombre} maxLength={LARGO_NOMBRE}
               autoComplete="off" placeholder="Almacén 1"
               onChange={(e) => onCambio({ ...valor, nombre: recortarNombre(e.target.value) })} />
        <p className="mt-1 flex justify-between gap-2 text-xs text-gray-500">
          <span>Lo que la terminal enseña en su pantalla.</span>
          <span aria-live="polite">{nombre.length}/{LARGO_NOMBRE}</span>
        </p>
        <Ayuda id="terminal.vincular.nombre" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// La pantalla
// ---------------------------------------------------------------------
export default function VincularTerminal({ contexto, irA, onCerrar }) {
  const precargado = normalizarCodigo(contexto?.vincular || '').codigo;
  const [pestana, setPestana] = useState('TERMINAL');

  // Paso 1
  const [codigo, setCodigo] = useState(precargado);
  const [rechazo, setRechazo] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [errorCodigo, setErrorCodigo] = useState(null);
  // Paso 2
  const [datos, setDatos] = useState(null);
  const [donde, setDonde] = useState({ id_sucursal: '', id_ubicacion: '', nombre: '' });
  const [aprobando, setAprobando] = useState(false);
  const [errorAprobar, setErrorAprobar] = useState(null);
  const [confirmarRechazo, setConfirmarRechazo] = useState(false);
  // Paso 3
  const [listo, setListo] = useState(null);

  const paso = listo ? 3 : datos ? 2 : 1;
  const vence = useCuentaRegresiva(datos?.vence_en);

  // ATRÁS RETROCEDE UN PASO, no sale de la pantalla (G3): del paso 2 vuelve
  // al código; en el 3 ya no hay nada que deshacer (la terminal ya es de la
  // empresa), así que atrás sale, como en los demás asistentes de la casa.
  // Y la pestaña «Tengo un código en la terminal» se cierra con atrás.
  usePaso('vincular-terminal', paso === 2 ? 1 : 0, (n) => {
    if (n === 0 && !listo) { setDatos(null); setErrorAprobar(null); setConfirmarRechazo(false); }
  });
  useSubpantalla('codigo-de-alta', pestana === 'ALTA', () => setPestana('TERMINAL'));

  const buscar = useCallback(async (cod) => {
    setBuscando(true);
    setErrorCodigo(null);
    try {
      const d = await api.get(`/terminal/v2/vincular/${cod}`);
      const sucursales = d.sucursales || [];
      const ubicaciones = d.ubicaciones || [];
      setDatos({ ...d, codigo: cod, sucursales, ubicaciones });
      setDonde({
        id_sucursal: sucursales.length === 1 ? String(sucursales[0].id) : '',
        id_ubicacion: ubicaciones.length === 1 ? String(ubicaciones[0].id) : '',
        nombre: recortarNombre(d.nombre_sugerido || ''),
      });
    } catch (e) {
      setErrorCodigo(leerErrorDelContrato(e, { porEstado: ERRORES_VER }));
    } finally {
      setBuscando(false);
    }
  }, []);

  // Con el código ya en la dirección (`/t/<código>`) no se le pide a la
  // persona que lo vuelva a teclear: se busca al montar, una sola vez.
  const buscado = useRef(null);
  useEffect(() => {
    if (!codigoCompleto(precargado) || buscado.current === precargado) return;
    buscado.current = precargado;
    buscar(precargado);
  }, [precargado, buscar]);

  function teclear(e) {
    const { codigo: limpio, rechazados } = normalizarCodigo(e.target.value);
    setCodigo(limpio);
    setRechazo(motivoDeRechazo(rechazados));
    setErrorCodigo(null);
  }

  async function aprobar(e) {
    e.preventDefault();
    setAprobando(true);
    setErrorAprobar(null);
    try {
      const d = await api.post(`/terminal/v2/vincular/${datos.codigo}/aprobar`, {
        id_sucursal: donde.id_sucursal ? Number(donde.id_sucursal) : null,
        id_ubicacion: Number(donde.id_ubicacion),
        nombre: recortarNombre(donde.nombre).trim(),
      });
      setListo(d);
    } catch (err) {
      setErrorAprobar(leerErrorDelContrato(err, { porEstado: ERRORES_APROBAR, terminal: 'esta terminal' }));
    } finally {
      setAprobando(false);
    }
  }

  async function rechazar() {
    setAprobando(true);
    setErrorAprobar(null);
    try {
      await api.post(`/terminal/v2/vincular/${datos.codigo}/rechazar`, {});
      setConfirmarRechazo(false);
      setDatos(null);
      setCodigo('');
      setErrorCodigo({ codigo: null, mensaje: 'La terminal quedó sin autorizar: su pantalla dirá «No autorizada».',
                       queHacer: 'En 30 segundos pedirá otro código. Si era tuya, tecléalo aquí cuando lo enseñe.' });
    } catch (err) {
      setErrorAprobar(leerErrorDelContrato(err, { porEstado: ERRORES_APROBAR }));
    } finally {
      setAprobando(false);
    }
  }

  function otraVez() {
    setListo(null); setDatos(null); setCodigo(''); setRechazo(null);
    setErrorCodigo(null); setErrorAprobar(null); setConfirmarRechazo(false);
  }

  function verTerminales() {
    if (onCerrar) onCerrar();
    else irA?.('terminales');
  }

  const faltan = LARGO_CODIGO - codigo.length;
  const falta2 = datos ? faltaEnDonde(donde, datos.ubicaciones) : null;
  const vencido = vence === 0;

  return (
    <div className="max-w-xl space-y-4" data-terminal="vincular">
      <header className="space-y-1">
        {onCerrar && (
          <button type="button" onClick={onCerrar} className={ENLACE}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Volver a mis terminales
          </button>
        )}
        <h1 className="text-xl font-bold text-gray-900">Agregar terminal</h1>
        <p className="text-sm text-gray-600">
          Da de alta una terminal de producto en tu empresa y dile dónde va a trabajar.
        </p>
      </header>
      <Ayuda id="terminal.vincular.pantalla" />

      <div role="tablist" aria-label="Cómo agregar la terminal" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {Object.entries(PESTANAS).map(([clave, rotulo]) => (
          <button key={clave} type="button" role="tab" aria-selected={pestana === clave}
                  onClick={() => setPestana(clave)}
                  className={`min-h-11 rounded-xl border px-3 text-sm font-semibold ${
                    pestana === clave
                      ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)] text-[var(--color-marca-oscura)]'
                      : 'border-gray-300 bg-white text-gray-700'}`}>
            {clave === 'TERMINAL'
              ? <QrCode className="mr-1 inline h-4 w-4" aria-hidden="true" />
              : <KeyRound className="mr-1 inline h-4 w-4" aria-hidden="true" />}
            {rotulo}
          </button>
        ))}
      </div>

      {pestana === 'ALTA' && <CodigoDeAlta />}

      {pestana === 'TERMINAL' && (
        <div role="tabpanel" className="space-y-4">
          <BarraPasos paso={paso} />

          {paso === 1 && (
            <form onSubmit={(e) => { e.preventDefault(); if (codigoCompleto(codigo)) buscar(codigo); }}
                  className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
              <div>
                <label htmlFor="vincular-codigo" className="block text-sm font-medium text-gray-700">
                  El código que enseña la pantalla de la terminal
                </label>
                <input id="vincular-codigo" className="entrada mt-1 text-center font-mono text-2xl tracking-widest"
                       value={codigo} onChange={teclear} inputMode="text" autoCapitalize="characters"
                       autoComplete="one-time-code" spellCheck={false} maxLength={LARGO_CODIGO + 2}
                       placeholder="K7P-4QX" disabled={buscando} />
                <Ayuda id="terminal.vincular.codigo" />
                {codigoCompleto(codigo) && (
                  <p className="mt-1 text-xs text-gray-600">En la terminal se ve así: <strong>{formatearCodigo(codigo)}</strong></p>
                )}
                {rechazo && <p role="alert" className="mt-1 text-sm text-red-700">{rechazo}</p>}
              </div>
              <ErrorDelContrato error={errorCodigo} />
              {buscando && <Cargando filas={2} etiqueta="Buscando la terminal" />}
              <Motivo id="motivo-buscar" cuando={!codigoCompleto(codigo) && !buscando}>
                {`Faltan ${faltan} ${faltan === 1 ? 'carácter' : 'caracteres'}: el código tiene ${LARGO_CODIGO}.`}
              </Motivo>
              <button type="submit" disabled={!codigoCompleto(codigo) || buscando}
                      aria-describedby={!codigoCompleto(codigo) ? 'motivo-buscar' : undefined} className={PRIMARIO}>
                {buscando ? 'Buscando…' : 'Buscar la terminal'}
              </button>
            </form>
          )}

          {paso === 2 && (
            <form onSubmit={aprobar} className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
              <div className="space-y-1 text-sm text-gray-700">
                <div className="font-semibold text-gray-900">Terminal encontrada</div>
                <div className="break-words">Serie {datos.serie}{datos.firmware ? ` · firmware ${datos.firmware}` : ''}</div>
                <div className="break-words">Código {formatearCodigo(datos.codigo)}</div>
                {vence !== null && (
                  <div role="status" className={vencido ? 'font-semibold text-red-700' : 'text-gray-600'}>
                    {vencido
                      ? 'Este código ya venció: la terminal enseña otro. Vuelve al paso 1 con el nuevo.'
                      : `Te quedan ${minutosYSegundos(vence)} para aprobarla.`}
                  </div>
                )}
              </div>

              <CamposDonde idBase="vincular" sucursales={datos.sucursales} ubicaciones={datos.ubicaciones}
                           valor={donde} onCambio={setDonde} />

              <ErrorDelContrato error={errorAprobar} />
              <Motivo id="motivo-aprobar" cuando={!!(falta2 || vencido) && !aprobando}>
                {vencido ? 'El código venció: pide el nuevo a la terminal.' : falta2}
              </Motivo>
              <button type="submit" disabled={!!falta2 || vencido || aprobando}
                      aria-describedby={falta2 || vencido ? 'motivo-aprobar' : undefined} className={PRIMARIO}>
                {aprobando ? 'Vinculando…' : 'Aprobar y vincular'}
              </button>

              {!confirmarRechazo ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button type="button" onClick={() => { setDatos(null); setErrorAprobar(null); }} className={SECUNDARIO}>
                    Cambiar el código
                  </button>
                  <button type="button" onClick={() => setConfirmarRechazo(true)} disabled={aprobando}
                          className={`${BOTON} border border-red-200 bg-white text-red-700`}>
                    No es mía: rechazarla
                  </button>
                </div>
              ) : (
                <div className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                  <p>La terminal verá «No autorizada» y pedirá otro código en 30 segundos. Nada de tu empresa le llega.</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <button type="button" onClick={rechazar} disabled={aprobando}
                            className={`${BOTON} bg-red-600 text-white`}>
                      <X className="mr-1 inline h-4 w-4" aria-hidden="true" /> Sí, rechazarla
                    </button>
                    <button type="button" onClick={() => setConfirmarRechazo(false)} className={SECUNDARIO}>No</button>
                  </div>
                </div>
              )}
            </form>
          )}

          {paso === 3 && (
            <div className="space-y-3 rounded-xl border-2 border-green-300 bg-green-50 p-4">
              <div role="status" className="flex items-start gap-2 text-sm text-green-900">
                <Check className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <div className="min-w-0 break-words">
                  <strong className="block">
                    {listo.mensaje || 'La terminal ya es de tu empresa. En unos segundos dirá «¡Lista!».'}
                  </strong>
                  <span>
                    Se llama «{listo.nombre || recortarNombre(donde.nombre)}»
                    {listo.codigo_terminal ? ` y su código de terminal es ${listo.codigo_terminal}` : ''}.
                  </span>
                </div>
              </div>
              <button type="button" onClick={verTerminales} className={PRIMARIO}>Ver mis terminales</button>
              <button type="button" onClick={otraVez} className={SECUNDARIO}>Agregar otra</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// «Tengo un código en la terminal» · contrato v2 §2.4
// ---------------------------------------------------------------------
const ERRORES_ALTA = { 404: 'CODIGO_DESCONOCIDO', 403: 'SIN_PERMISO', 409: 'TOPE_DEL_PLAN', 429: 'DEMASIADAS_PETICIONES' };

export function CodigoDeAlta() {
  const [catalogos, setCatalogos] = useState(null);
  const [donde, setDonde] = useState({ id_sucursal: '', id_ubicacion: '', nombre: '' });
  const [pidiendo, setPidiendo] = useState(false);
  const [error, setError] = useState(null);
  const [codigo, setCodigo] = useState(null);
  const vence = useCuentaRegresiva(codigo?.vence_en);

  useEffect(() => {
    let vivo = true;
    Promise.all([api.get('/sucursales').catch(() => []), api.get('/ubicaciones').catch(() => [])])
      .then(([sucursales, ubicaciones]) => {
        if (!vivo) return;
        const s = Array.isArray(sucursales) ? sucursales : [];
        const u = Array.isArray(ubicaciones) ? ubicaciones : [];
        setCatalogos({ sucursales: s, ubicaciones: u });
        setDonde((d) => ({ ...d,
          id_sucursal: s.length === 1 ? String(s[0].id) : d.id_sucursal,
          id_ubicacion: u.length === 1 ? String(u[0].id) : d.id_ubicacion }));
      });
    return () => { vivo = false; };
  }, []);

  async function pedir(e) {
    e?.preventDefault?.();
    setPidiendo(true);
    setError(null);
    try {
      const d = await api.post('/terminal/v2/codigos-de-alta', {
        id_sucursal: donde.id_sucursal ? Number(donde.id_sucursal) : null,
        id_ubicacion: Number(donde.id_ubicacion),
        nombre: recortarNombre(donde.nombre).trim(),
      });
      setCodigo(d);
    } catch (err) {
      setError(leerErrorDelContrato(err, { porEstado: ERRORES_ALTA, terminal: 'esta terminal' }));
    } finally {
      setPidiendo(false);
    }
  }

  const falta = catalogos ? faltaEnDonde(donde, catalogos.ubicaciones) : null;
  const vencido = vence === 0;

  return (
    <div role="tabpanel" className="space-y-4" data-terminal="codigo-de-alta">
      <p className="text-sm text-gray-600">
        Para una terminal sin cámara cerca: TEGO te da un número de 6 dígitos y lo tecleas en la terminal, en su
        opción «Tengo un código».
      </p>
      <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900">
        Genera el código con la terminal ya delante: vale 10 minutos y una sola vez.
      </p>

      {!codigo && (
        <form onSubmit={pedir} className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
          {!catalogos && <Cargando filas={3} etiqueta="Cargando sucursales y repisas" />}
          {catalogos && (
            <CamposDonde idBase="alta" sucursales={catalogos.sucursales} ubicaciones={catalogos.ubicaciones}
                         valor={donde} onCambio={setDonde} />
          )}
          <ErrorDelContrato error={error} />
          <Motivo id="motivo-alta" cuando={!!falta && !pidiendo}>{falta}</Motivo>
          <button type="submit" disabled={!catalogos || !!falta || pidiendo}
                  aria-describedby={falta ? 'motivo-alta' : undefined} className={PRIMARIO}>
            {pidiendo ? 'Pidiendo…' : 'Darme el código'}
          </button>
          <Ayuda id="terminal.vincular.codigo_de_alta" />
        </form>
      )}

      {codigo && (
        <div className="space-y-3 rounded-xl border-2 border-green-300 bg-green-50 p-4 text-center">
          <div className="text-sm font-semibold text-green-900">Teclea este número en la terminal</div>
          <div className="break-all font-mono text-5xl font-bold tracking-widest text-green-900" aria-live="polite">
            {codigo.codigo}
          </div>
          <div role="status" className={`text-sm ${vencido ? 'font-semibold text-red-700' : 'text-green-900'}`}>
            {vencido ? 'Este número ya venció. Pide otro.' : `Vence en ${minutosYSegundos(vence ?? 600)}`}
          </div>
          <Ayuda id="terminal.vincular.codigo_de_alta" />
          <ErrorDelContrato error={error} />
          <button type="button" onClick={pedir} disabled={pidiendo} className={PRIMARIO}>
            <RefreshCw className="mr-1 inline h-4 w-4" aria-hidden="true" /> {pidiendo ? 'Pidiendo…' : 'Dame otro'}
          </button>
          <button type="button" onClick={() => { setCodigo(null); setError(null); }} className={SECUNDARIO}>
            Ya lo tecleé
          </button>
        </div>
      )}
    </div>
  );
}
